import React from 'react';

/** Built-in launcher icons — keys must match widget.js / backend LAUNCHER_ICONS. */
export const LAUNCHER_ICON_OPTIONS = [
  { key: 'chat', label: 'Chat' },
  { key: 'headset', label: 'Headset' },
  { key: 'lifebuoy', label: 'Lifebuoy' },
  { key: 'question', label: 'Question' },
  { key: 'message', label: 'Message' },
  { key: 'mail', label: 'Mail' },
  { key: 'help', label: 'Help' },
  { key: 'support', label: 'Support' },
];

const PATHS = {
  chat: 'M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z',
  headset: null,
  lifebuoy: null,
  question: null,
  message: null,
  mail: null,
  help: null,
  support: null,
};

/** Inline SVG preview used by the admin icon picker. */
export function LauncherIcon({ name = 'chat', color = '#FFFFFF', size = 22 }) {
  const c = color || '#FFFFFF';
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: c,
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };

  switch (name) {
    case 'headset':
      return (
        <svg {...common}>
          <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
          <path d="M4 14a2 2 0 0 0 2 2h1v-4H6a2 2 0 0 0-2 2z" />
          <path d="M20 14a2 2 0 0 1-2 2h-1v-4h1a2 2 0 0 1 2 2z" />
          <path d="M15 18a3 3 0 0 1-6 0" />
        </svg>
      );
    case 'lifebuoy':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="3" />
          <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
        </svg>
      );
    case 'question':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M9.1 9a3 3 0 1 1 4.8 2.4c-.8.6-1.4 1-1.4 2.1" />
          <circle cx="12" cy="17" r="0.6" fill={c} stroke="none" />
        </svg>
      );
    case 'message':
      return (
        <svg {...common}>
          <path d="M4 5h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H9l-5 4V6a1 1 0 0 1 1-1z" />
          <path d="M8 10h8M8 13h5" />
        </svg>
      );
    case 'mail':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 7l9 7 9-7" />
        </svg>
      );
    case 'help':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M9.1 9a3 3 0 1 1 5.8 1.2c0 2-3 2.2-3 4" />
          <circle cx="12" cy="17" r="0.6" fill={c} stroke="none" />
        </svg>
      );
    case 'support':
      return (
        <svg {...common}>
          <circle cx="12" cy="8" r="3" />
          <path d="M5 20v-1a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v1" />
          <path d="M4 12v-1a8 8 0 0 1 16 0v1" />
        </svg>
      );
    case 'chat':
    default:
      return (
        <svg {...common}>
          <path d={PATHS.chat} />
        </svg>
      );
  }
}

/** Live preview of the floating launcher as end users will see it. */
export function LauncherPreview({
  mode = 'text',
  label = 'Support',
  icon = 'chat',
  iconColor = '#FFFFFF',
  bgColor = '#0F766E',
  compact = false,
}) {
  const isIcon = mode === 'icon';
  if (compact) {
    const letter = String(label || 'S').trim().charAt(0).toUpperCase() || 'S';
    return (
      <div
        aria-hidden
        style={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: 10,
          background: bgColor || '#0F766E',
          color: iconColor || '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          fontWeight: 700,
          lineHeight: 1,
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.12)',
          userSelect: 'none',
        }}
      >
        {isIcon
          ? <LauncherIcon name={icon} color={iconColor} size={18} />
          : letter}
      </div>
    );
  }
  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      background: bgColor || '#0F766E',
      color: iconColor || '#FFFFFF',
      borderRadius: isIcon ? '50%' : 999,
      width: isIcon ? 56 : 'auto',
      height: isIcon ? 56 : 'auto',
      padding: isIcon ? 0 : '12px 18px',
      fontSize: 14,
      fontWeight: 600,
      boxShadow: '0 8px 24px rgba(0,0,0,.18)',
      userSelect: 'none',
    }}
    >
      {isIcon
        ? <LauncherIcon name={icon} color={iconColor} size={26} />
        : (label || 'Support')}
    </div>
  );
}

export function ColorField({ label, value, onChange, labelStyle, inputStyle }) {
  const safe = /^#[0-9A-Fa-f]{6}$/.test(value || '') ? value : '#0F766E';
  return (
    <div>
      <span style={labelStyle}>{label}</span>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input type="color" value={safe} onChange={(e) => onChange(e.target.value)} />
        <input
          style={{ ...inputStyle, width: 110 }}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#0F766E"
        />
      </div>
    </div>
  );
}

/**
 * Text XOR Icon launcher configuration block.
 * Controlled entirely from Product Management → Client widgets (super admin).
 */
export function LauncherConfigFields({
  value,
  onChange,
  labelStyle,
  inputStyle,
  title = 'Launcher button',
}) {
  const mode = value.launcher_mode === 'icon' ? 'icon' : 'text';
  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12,
      padding: 12, background: '#F0FDFA', borderRadius: 10, border: '1px solid #99F6E4',
    }}
    >
      <div style={{ gridColumn: '1 / -1', fontSize: 12, fontWeight: 600, color: '#0F766E' }}>
        {title} — text or icon (only one), colors controlled from ITSM
      </div>

      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {[
          { key: 'text', label: 'Text only' },
          { key: 'icon', label: 'Icon only' },
        ].map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => set({ launcher_mode: opt.key })}
            style={{
              padding: '7px 14px',
              borderRadius: 8,
              border: mode === opt.key ? '2px solid #0F766E' : '1px solid #D1D5DB',
              background: mode === opt.key ? '#CCFBF1' : '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              color: '#134E4A',
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {mode === 'text' ? (
        <div style={{ gridColumn: '1 / -1', maxWidth: 320 }}>
          <span style={labelStyle}>Button text</span>
          <input
            style={inputStyle}
            value={value.launcher_label || ''}
            onChange={(e) => set({ launcher_label: e.target.value })}
            placeholder="Support"
          />
        </div>
      ) : (
        <div style={{ gridColumn: '1 / -1' }}>
          <span style={labelStyle}>Choose icon</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
            {LAUNCHER_ICON_OPTIONS.map((opt) => {
              const active = (value.launcher_icon || 'chat') === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  title={opt.label}
                  onClick={() => set({ launcher_icon: opt.key })}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    border: active ? '2px solid #0F766E' : '1px solid #D1D5DB',
                    background: active ? '#CCFBF1' : '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <LauncherIcon name={opt.key} color="#0F766E" size={22} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      <ColorField
        label="Background color"
        value={value.launcher_bg_color || value.primary_color || '#0F766E'}
        onChange={(v) => set({ launcher_bg_color: v })}
        labelStyle={labelStyle}
        inputStyle={inputStyle}
      />
      {mode === 'icon' && (
        <ColorField
          label="Icon color"
          value={value.launcher_icon_color || '#FFFFFF'}
          onChange={(v) => set({ launcher_icon_color: v })}
          labelStyle={labelStyle}
          inputStyle={inputStyle}
        />
      )}
      {mode === 'text' && (
        <ColorField
          label="Text color"
          value={value.launcher_icon_color || '#FFFFFF'}
          onChange={(v) => set({ launcher_icon_color: v })}
          labelStyle={labelStyle}
          inputStyle={inputStyle}
        />
      )}

      <div>
        <span style={labelStyle}>Preview</span>
        <div style={{ marginTop: 6 }}>
          <LauncherPreview
            mode={mode}
            label={value.launcher_label}
            icon={value.launcher_icon}
            iconColor={value.launcher_icon_color}
            bgColor={value.launcher_bg_color || value.primary_color}
          />
        </div>
      </div>
    </div>
  );
}
